import type { QueryClient } from "@tanstack/react-query";
import type { Channel, SeriesItem, VodItem } from "@hybrid-iptv/types";
import { NAME_MATCH_VERSION } from "@hybrid-iptv/epg-parser";
import { api, runsOnDevice } from "../api/client.js";
import { fetchChannels, fetchEpgByName, fetchEpgFor, providerBody } from "../epg/api.js";
import { catalogCacheKey, readCatalogCache, writeCatalogCache } from "../storage/catalogCache.js";
import { dbClear, dbDelete, dbKeys } from "../storage/db.js";
import { writeEpgCache } from "../epg/epgCache.js";
import { useChannelHealthStore } from "./channelHealthStore.js";
import { channelCheckAvailable, checkChannelOnDevice } from "./channelCheck.js";
import { usePlayerStore } from "../player/playerStore.js";
import { isProbeableChannel, runHealthProbe } from "./runHealthProbe.js";
import { useSyncSettings } from "./syncSettingsStore.js";

/**
 * Section 12 background refresh: re-pull EPG, VOD, and Series into the device
 * IndexedDB caches so a cold mount paints the newest data even when no screen
 * is watching those queries. Steps run SEQUENTIALLY — each one opens provider
 * connections, and a parallel burst would eat the account's ~5-connection cap
 * and stall live playback. One failing step never kills the rest.
 */
export async function refreshCatalogNow(sourceId: string, force = false): Promise<void> {
  let channels: Channel[] | null = null;
  try {
    channels = await fetchChannels(sourceId, force);
    await writeCatalogCache("channels", catalogCacheKey("channels", sourceId), channels, (c) => c.length);
  } catch {
    // skip — a failed channels refresh shouldn't block VOD/EPG
  }
  try {
    if (runsOnDevice()) {
      // Stage B: the APK's screens ask native for what they show, so a sync
      // refreshes the native store and never brings the catalog to the page.
      await api.post(`/api/sources/${sourceId}/vod/refresh`, { ...providerBody(), force });
    } else {
      const data = await api.post<{ vod: VodItem[]; series: SeriesItem[] }>(`/api/sources/${sourceId}/vod`, { ...providerBody(), ...(force ? { force: true } : {}) });
      await writeCatalogCache("vod", catalogCacheKey("vod", sourceId), data, (d) => d.vod.length + d.series.length);
    }
  } catch {
    // skip
  }
  if (channels && channels.length > 0) {
    try {
      const names = [...new Set(channels.map((c) => c.name.toLowerCase().trim()).filter(Boolean))];
      if (names.length > 0 && runsOnDevice()) {
        // The APK keeps no copy of the guide (Phase 3 step 2b): this only has the
        // TV refresh its own when it is due, and match the names, with no programmes.
        await fetchEpgFor(sourceId, names, [], 4);
      } else if (names.length > 0) {
        const epg = await fetchEpgByName(sourceId, names, 4);
        await writeEpgCache(sourceId, 4, epg);
      }
    } catch {
      // skip
    }
  }
  useSyncSettings.getState().markUpdated();
}

/**
 * The APK kept a whole-catalog copy in IndexedDB before stage B. Nothing on the
 * device reads it any more (the website still does, in the browser), so it is
 * dropped: on a TV it is tens of megabytes of storage for nothing.
 */
export async function dropDeviceCatalogCopy(): Promise<void> {
  if (!runsOnDevice()) return;
  try {
    await dbClear("vod");
  } catch {
    // Storage is best-effort; the copy is simply left behind.
  }
}

/**
 * The APK kept whole-guide copies in IndexedDB (epg/epgCache.ts) until Phase 3
 * step 2 had every screen ask the TV's guide for its own channels. Nothing on
 * the device reads them any more, so they are dropped: tens of megabytes. Only
 * the copies ("<source>|<hours>"): the name matches in the same store
 * ("names<version>:…", api/nativeGuide.ts) are still read. Matches saved by
 * an older matcher are never read again, so they go too.
 */
export async function dropDeviceGuideCopy(): Promise<void> {
  if (!runsOnDevice()) return;
  try {
    for (const key of await dbKeys("epgCache")) if (key.includes("|") || key.startsWith("match:") || staleNameMatch(key)) await dbDelete("epgCache", key);
  } catch {
    // Storage is best-effort; the copies are simply left behind.
  }
}

const staleNameMatch = (key: string) => /^names\d*:/.test(key) && !key.startsWith(`names${NAME_MATCH_VERSION}:`);

/** Wake mounted views from their stale in-memory caches after a refresh. */
export function invalidateCatalogQueries(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: ["channels"] });
  queryClient.invalidateQueries({ queryKey: ["vod"] });
  // Stage B: the screens' catalog queries and genre lists (useCatalogQuery.ts).
  queryClient.invalidateQueries({ queryKey: ["catalogQuery"] });
  queryClient.invalidateQueries({ queryKey: ["catalogCategories"] });
  queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === "epg" });
}

/** Margin added to the server's own retry hint before the guide asks again. */
const PROBE_RETRY_MARGIN_S = 5;

/**
 * How long to wait for the next health cycle, given the seconds the server named.
 * The floor keeps a malformed hint from scheduling a hot loop; the margin keeps the next request
 * from landing a moment early and being skipped for nothing. Exported for tests.
 */
export function healthCycleRetryDelayMs(retryAfterSeconds: number): number {
  return Math.max(1, retryAfterSeconds + PROBE_RETRY_MARGIN_S) * 1000;
}

let healthCycleTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * The allowance ran out partway through a batch, so the guide paces itself: one more cycle when
 * the server said the allowance is back, instead of asking again on the next refresh and being
 * skipped for free. Only chained when the last cycle probed SOMETHING — otherwise a wasted
 * request would chain to the next wasted request. A fresh (manual) cycle cancels the pending one.
 */
function scheduleHealthCycle(queryClient: QueryClient, sourceId: string, retryAfterSeconds: number): void {
  if (healthCycleTimer !== null) clearTimeout(healthCycleTimer);
  healthCycleTimer = setTimeout(() => {
    healthCycleTimer = null;
    void healthCycleNow(queryClient, sourceId);
  }, healthCycleRetryDelayMs(retryAfterSeconds));
}

/**
 * One health cycle (owner, 2026-10-21): test the channels hidden for failing again, after each
 * playlist update, and bring back every one that answers. A check never hides anything; only
 * failed tries do (channelHealthStore).
 *
 * Inside the APK the TV checks them itself, one at a time and only while nothing plays, so a
 * check never takes a connection from the viewer; when something is playing, the batch waits for
 * the player to close. The website asks the server (POST /api/health/probe), paced by the
 * allowance it names.
 *
 * Reads the channel list from the device IndexedDB cache (just written by the
 * refresh) rather than the React Query cache — a background job must not
 * depend on which page happens to be mounted, and the query cache holds the
 * selected { channels, groups } shape anyway. */
export async function healthCycleNow(queryClient: QueryClient, sourceId: string): Promise<void> {
  if (healthCycleTimer !== null) {
    // A fresh cycle supersedes any pending one — never leave two chains running.
    clearTimeout(healthCycleTimer);
    healthCycleTimer = null;
  }
  const health = useChannelHealthStore.getState();
  const entry = await readCatalogCache<Channel[]>("channels", catalogCacheKey("channels", sourceId));
  const channels = entry?.data ?? [];
  const hidden = channels.filter((c) => health.health[c.id]?.state === "dead");
  if (hidden.length === 0) return;

  if (channelCheckAvailable()) {
    // Oldest check first, so a long list is worked through over several updates.
    const order = [...hidden].sort(
      (a, b) => Date.parse(health.health[a.id]?.lastCheckedAt ?? "") - Date.parse(health.health[b.id]?.lastCheckedAt ?? ""),
    );
    await recheckOnDevice(order.slice(0, DEVICE_RECHECK_CAP));
    return;
  }

  // Paced: the previous cycle was told when the allowance returns. Asking before then is a
  // request the server can only refuse (2026-09-24: the sweep spent most of the minute and
  // starved a viewer's tune, so the guide now takes its small share and waits its turn).
  if (health.nextProbeAt !== null && Date.now() < health.nextProbeAt) return;
  const probeable = hidden.filter((c) => isProbeableChannel(c.streamUrl));
  // Rotation first: channels the recent cycles could NOT reach come first (the allowance pays
  // for a handful at a time) — never more than the cap per cycle.
  const seen = new Set(health.recentProbeIds);
  const sorted = [...probeable].sort((a, b) => Number(seen.has(a.id)) - Number(seen.has(b.id)));
  const targets = sorted.slice(0, HEALTH_PROBE_CAP).map((c) => ({ channelId: c.id, url: c.streamUrl }));
  if (targets.length === 0) return;
  const outcome = await runHealthProbe(targets);
  // Some channels could not be paid for: come back when the server said the allowance returns,
  // while progress is still being made. Staying quiet instead would leave the guide's health
  // frozen wherever the allowance happened to run out.
  if (outcome.skipped > 0 && outcome.probedIds.length > 0) {
    scheduleHealthCycle(queryClient, sourceId, outcome.retryAfterSeconds);
  }
}

const HEALTH_PROBE_CAP = 30;
/** A TV re-test takes 1-2 s a channel (8 s at most): a hundred is a few minutes, in the background. */
const DEVICE_RECHECK_CAP = 100;

/** The player holds a connection whenever it has something loaded, the guide's preview included. */
function playerBusy(): boolean {
  return usePlayerStore.getState().source !== null;
}

let waitingRecheck: Channel[] | null = null;
let stopWaiting: (() => void) | null = null;

/** Check each channel in turn; stop the moment something starts playing and finish when it closes. */
async function recheckOnDevice(channels: Channel[]): Promise<void> {
  for (let i = 0; i < channels.length; i++) {
    if (playerBusy()) {
      waitForIdlePlayer(channels.slice(i));
      return;
    }
    const channel = channels[i]!;
    const result = await checkChannelOnDevice(channel.streamUrl);
    useChannelHealthStore.getState().recordProbeResults([{ channelId: channel.id, result }]);
  }
}

function waitForIdlePlayer(rest: Channel[]): void {
  waitingRecheck = rest;
  if (stopWaiting) return;
  stopWaiting = usePlayerStore.subscribe((state) => {
    if (state.source !== null || !waitingRecheck) return;
    const next = waitingRecheck;
    waitingRecheck = null;
    stopWaiting?.();
    stopWaiting = null;
    void recheckOnDevice(next);
  });
}

/** "Try now" on one hidden channel: asked for by hand, so it runs even while something plays. */
export async function retestChannelNow(channel: Channel): Promise<boolean> {
  if (channelCheckAvailable()) {
    const result = await checkChannelOnDevice(channel.streamUrl);
    useChannelHealthStore.getState().recordProbeResults([{ channelId: channel.id, result }]);
    return result.ok;
  }
  // The website: the server's probe records its own answer (runHealthProbe).
  await runHealthProbe([{ channelId: channel.id, url: channel.streamUrl }]);
  return useChannelHealthStore.getState().health[channel.id]?.state === "good";
}
